/**
 * Ce que l'apprenant doit lire d'une formation (#74).
 *
 * Trois écrans posent les mêmes questions — quel est l'état de cette formation,
 * par quelle étape reprendre, combien en reste-t-il — et les posaient chacun à
 * leur façon, dans leur JSX. Les réponses sont regroupées ici, en fonctions
 * pures : une règle qui vit en trois exemplaires dérive au premier ajustement,
 * et celle-ci décide de ce qu'on affiche comme « terminé » à quelqu'un dont
 * c'est l'obligation professionnelle.
 *
 * ⚠️ Aucune de ces fonctions ne décide d'un ACCÈS. Le verrou reste évalué côté
 * API par `module-unlock.ts`, seul point d'évaluation (ADR 0002 § 3) ; on ne
 * fait que relire `unlocked` / `lockReason` pour les mettre en mots.
 */

import type { CertificateLockReason } from '@zeus/index'

/** Un chapitre de parcours (#75), tel que l'API le sert. */
export type SectionView = { id: string, title: string, position: number }

export type CompletionStatus = 'COMPLETED' | 'INCOMPLETE' | 'NOT_ATTEMPTED'

/** Ce qu'il faut d'une étape pour décider, rien de plus. */
export type StepView = {
  id: string
  position: number
  unlocked?: boolean | null
  lockReason?: string | null
  unlockAt?: unknown
  /** Module (id de `ScormModule`) qui ouvre celui-ci (#80), et le score minimum à y obtenir, sur [0..1]. */
  prerequisiteModuleId?: string | null
  prerequisiteMinScore?: number | null
  /** Le parcours permet-il de refaire ce module une fois terminé (#84) ; absent = oui. */
  allowRetake?: boolean | null
  /** Chapitre de l'étape (#75), ou null. */
  sectionId?: string | null
  module?: {
    id: string
    title?: string | null
    description?: string | null
    status?: string | null
    /** Durée estimée en minutes, lue dans le manifeste. Nulle = inconnue (#74). */
    durationMinutes?: number | null
    /** Seuil de réussite normalisé [0..1], ou null si le paquet n'en déclare pas. */
    masteryScore?: number | null
  } | null
  myTracking?: {
    completionStatus?: string | null
    scoreRaw?: number | null
    scoreMax?: number | null
    completedAt?: unknown
    /** Temps réellement passé, converti en secondes côté API (#74). */
    totalTimeSeconds?: number | null
  } | null
}

export type SessionView = {
  id: string
  name: string
  modules?: StepView[] | null
  /** Chapitres du parcours (#75), dans l'ordre ; vide = aucun. */
  sections?: SectionView[] | null
  progress?: {
    totalModules?: number | null
    completedModules?: number | null
    completed?: boolean | null
  } | null
  /** L'attestation, évaluée par l'API (#10) ; absente hors du contexte d'un apprenant. */
  certificate?: {
    unlocked: boolean
    reason?: `${CertificateLockReason}` | null
    blockingModuleIds: string[]
  } | null
  /** Seuil sur [0..1] quand l'attestation exige un score (#10). */
  certificateMinScore?: number | null
}

/** Les étapes, dans l'ordre du parcours. L'API les sert déjà triées. */
export function steps(session: SessionView): StepView[] {
  return session.modules ?? []
}

/**
 * Une formation d'un seul module est un COURS, au-delà c'est un PARCOURS.
 *
 * Distinction volontairement dérivée et non stockée : ajouter un type en base
 * obligerait le formateur à le déclarer, et à le corriger le jour où il ajoute
 * un module. Le nombre d'étapes dit déjà la vérité.
 */
export function isPath(session: SessionView): boolean {
  return steps(session).length > 1
}

/**
 * Lien de lancement d'un COURS : direct, quand l'API a ouvert son module et que
 * son paquet est prêt. Undefined pour un parcours, qui passe par son écran, et
 * pour un cours fermé — sa carte le dit, sa recherche mène à son écran (#109).
 */
export function courseLaunchHref(session: SessionView): string | undefined {
  const single = steps(session)[0]
  if (isPath(session) || single?.unlocked !== true || single.module?.status !== 'READY') return undefined
  return `/scorm/${session.id}/${single.module.id}`
}

/**
 * Où mène une formation depuis l'accueil : l'écran d'un parcours, ou le module
 * d'un cours, qui se lance sans écran intermédiaire. Rien pour un cours fermé.
 */
export function sessionHref(session: SessionView): string | undefined {
  return isPath(session) ? `/my-sessions/${session.id}` : courseLaunchHref(session)
}

/**
 * Le sous-titre d'une carte, sauf s'il ne fait que répéter son titre (#307) : un
 * cours dont le module porte le nom de la formation affichait deux fois la même
 * ligne. La comparaison ignore la casse et les espaces.
 */
export function distinctSubtitle(title: string, subtitle?: string | null): string | null {
  const normalize = (text: string) => text.trim().replace(/\s+/g, ' ').toLocaleLowerCase()
  if (!subtitle?.trim() || normalize(subtitle) === normalize(title)) return null
  return subtitle
}

/**
 * État de la formation entière.
 *
 * `progress.completed` vient de l'API, seul endroit qui décide de ce que
 * « terminé » veut dire : un parcours de cinq modules dont un est fini n'est
 * pas terminé, et le déduire ici d'un `myTracking` isolé redirait le contraire.
 */
export function completionStatus(session: SessionView): CompletionStatus {
  if (session.progress?.completed === true) return 'COMPLETED'
  const done = session.progress?.completedModules ?? 0
  const touched = done > 0 || steps(session).some(s => s.myTracking != null)
  return touched ? 'INCOMPLETE' : 'NOT_ATTEMPTED'
}

/**
 * État d'une étape, pour l'apprenant. Un suivi existe dès la première
 * ouverture, avant toute complétion remontée : l'étape est alors commencée.
 */
export function stepStatus(step: StepView): CompletionStatus {
  if (step.myTracking?.completionStatus === 'COMPLETED') return 'COMPLETED'
  return step.myTracking != null ? 'INCOMPLETE' : 'NOT_ATTEMPTED'
}

/** Ce que le bouton d'une étape propose (#84). */
export type StepAction = 'start' | 'resume' | 'retake'

/** Libellé de chaque action. */
export const STEP_ACTION_KEYS: Record<StepAction, string> = {
  start: 'sessions.my.start',
  resume: 'home.resume',
  retake: 'home.retake',
}

/**
 * L'action proposée sur une étape : la commencer, la reprendre, ou la refaire
 * une fois terminée — relancer un module terminé, c'est le refaire (#84). Rien
 * quand le parcours interdit de refaire : un module terminé s'y ferme. Un module
 * en cours se reprend toujours.
 *
 * Ne dit rien de l'OUVERTURE de l'étape (`unlocked`), que l'appelant vérifie.
 */
export function stepAction(step: StepView): StepAction | null {
  const status = stepStatus(step)
  if (status === 'COMPLETED') return step.allowRetake === false ? null : 'retake'
  return status === 'INCOMPLETE' ? 'resume' : 'start'
}

/**
 * L'étape par laquelle reprendre : la première ouverte et non terminée.
 *
 * C'est ce que dit le bouton « Reprendre », et ce que la carte annonce en
 * « prochaine étape ». Rendre la première étape NON TERMINÉE sans regarder son
 * verrou proposerait une porte fermée — le cas exact d'un parcours programmé
 * dont l'étape suivante n'ouvre que la semaine prochaine.
 */
export function nextStep(session: SessionView): StepView | null {
  return steps(session).find(
    s => s.unlocked === true
      && s.myTracking?.completionStatus !== 'COMPLETED'
      && s.module?.status === 'READY',
  ) ?? null
}

/**
 * Étapes restantes, verrouillées comprises : c'est le travail qui reste, pas
 * celui qui est accessible aujourd'hui.
 */
export function remainingSteps(session: SessionView): number {
  const total = session.progress?.totalModules ?? steps(session).length
  return Math.max(0, total - (session.progress?.completedModules ?? 0))
}

/** Avancement en pourcentage entier, 0 quand la formation n'a aucune étape. */
export function progressPct(session: SessionView): number {
  const total = session.progress?.totalModules ?? steps(session).length
  if (!total) return 0
  return Math.round(((session.progress?.completedModules ?? 0) / total) * 100)
}

/** Temps réellement passé sur la formation, en secondes : la somme de ses étapes suivies. */
export function timeSpentSeconds(session: SessionView): number {
  return steps(session).reduce((sum, step) => sum + (step.myTracking?.totalTimeSeconds ?? 0), 0)
}

/**
 * Date de fin d'une formation terminée : celle de sa dernière étape terminée.
 * `null` tant que l'API ne la dit pas terminée — une étape finie ne termine pas un parcours.
 */
export function finishedAt(session: SessionView): Date | null {
  if (completionStatus(session) !== 'COMPLETED') return null
  const times = steps(session)
    .map(step => step.myTracking?.completedAt)
    .filter(value => value != null)
    .map(value => new Date(value as string | Date).getTime())
  return times.length ? new Date(Math.max(...times)) : null
}

/**
 * Seuil de réussite d'une étape, ramené sur l'échelle de son score.
 *
 * Le manifeste le déclare normalisé [0..1] ; le score, lui, est sur l'échelle
 * du module. Les remettre sur la même échelle fait qu'ils se comparent à l'œil :
 * « 82/100, seuil 70 », pas « 82 et 0,7 ». Null si le paquet n'en déclare pas.
 */
export function stepThreshold(step: StepView): number | null {
  const mastery = step.module?.masteryScore
  if (mastery == null) return null
  return Math.round(mastery * (step.myTracking?.scoreMax ?? 100))
}

export type StepScore = {
  raw: number
  max: number | null
  threshold: number | null
  /** Seuil atteint ou non ; null quand il n'y a pas de seuil à qui se comparer. */
  passed: boolean | null
}

/** Score d'une étape face à son seuil (#90), ou null si l'étape n'est pas notée. */
export function stepScore(step: StepView): StepScore | null {
  const raw = step.myTracking?.scoreRaw
  if (raw == null) return null
  const threshold = stepThreshold(step)
  return {
    raw,
    max: step.myTracking?.scoreMax ?? null,
    threshold,
    passed: threshold == null ? null : raw >= threshold,
  }
}
